import React, { useEffect, useRef, useState } from "react";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import { createPortal } from "react-dom";
import {
  Direction,
  Flex,
  OverlayStyle,
  SlideoutDrawerV2,
  Switch,
  Tab,
  Tabs,
  Tag,
  Text,
  type TagVariant,
} from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { PackageCapability } from "#graphql/supergraphEnums";
import { useActivityTabEnabled } from "#hooks/useActivityTabFlag";
import { useDownloadPackageDocument } from "#hooks/useDownloadPackageDocument";
import { useFayAuthGateArmed } from "#hooks/useFayAuthGate";
import { useFayPackageStaging } from "#hooks/useFayPackageStaging";
import { hasPackageCapability, usePackageDetail } from "#hooks/usePackageDetail";
import { useRemoveDocument } from "#hooks/useRemoveDocument";
import { useUpdatePackageState } from "#hooks/useUpdatePackageState";
import {
  getPackageStatusPresentation,
  packageStatus,
  packageSummary,
  type PackageStatusPresentation,
} from "#utils/checksStatus";
import { docTypeLabel } from "#utils/classificationGrouping";
import { formatReportingDate } from "#utils/coverageGrid.utils";
import { PUBLISH_STATE_LABEL } from "#utils/publishState";
import { ActivityTab } from "../ActivityTab";
import { ChecksTab } from "../ChecksTab";
import { PackageDocumentsTab } from "../PackageDocumentsTab";
import { PdfViewer } from "../PdfViewer";
import { RemovePackageDocumentConfirmModal } from "../RemovePackageDocumentConfirmModal";
import { SummaryTab } from "../SummaryTab";
import * as styles from "./styles_vanilla.css";
import type { PackageState } from "#graphql/supergraphTypes";
import type { PackageTab } from "#hooks/usePackageRoute";
import type { PackageDocument } from "#viewModelTypes";

export type PackageDetailSlideoutProps = {
  /** Package id to load, or null when the slideout is closed. */
  packageId: string | null;
  activeTab: PackageTab;
  onTabChange: (tab: PackageTab) => void;
  onClose: () => void;
  /** Fired once when the server confirms the package doesn't exist or isn't visible. */
  onNotFound: () => void;
  /** Arena the package read and its document writes are scoped to. */
  arenaId: number | undefined;
};

/** Display labels for fay package types. MVP only ships `PeriodClose`; unknown
 *  types fall back to the raw value rather than masking a backend change. */
const PACKAGE_TYPE_LABELS: Record<string, string> = {
  PeriodClose: "Period close",
};

const packageTypeLabel = (packageType: string): string =>
  PACKAGE_TYPE_LABELS[packageType] ?? packageType;

/** Names the consequence rather than the mechanism, which is what the toggle
 *  actually decides. Must not read "Published" — that is the status tag's text,
 *  and the two sit in the same header. */
export const PUBLISH_SWITCH_LABEL = "Publish to clients";

type PublishStateTag = { variant: TagVariant; label: string };

const PUBLISH_STATE_TAGS: Record<PackageState, PublishStateTag> = {
  PUBLISHED: { variant: "success", label: PUBLISH_STATE_LABEL.PUBLISHED },
  // informative, not neutral: neutral is the variant for non-semantic content,
  // and this state gates what a client can see.
  UNPUBLISHED: { variant: "informative", label: PUBLISH_STATE_LABEL.UNPUBLISHED },
};

/** A publish state this build doesn't know reads as itself rather than blanking
 *  the header, the same fallback `packageTypeLabel` takes. */
const publishStateTag = (state: PackageState): PublishStateTag =>
  PUBLISH_STATE_TAGS[state] ?? { variant: "neutral", label: state };

/** A single header stat (value over a label), e.g. "8" / "Documents" or
 *  "10 of 19" / "Checks passed". `valueClassName` tints the value, e.g. for a
 *  triage-count stat that should read as neutral at zero. */
const HeaderStat: React.FC<{
  label: string;
  value: React.ReactNode;
  valueClassName?: string;
}> = ({ label, value, valueClassName }) => (
  <Flex flexDirection="column" gap={2}>
    <Text variant="strong-lg" asSpan className={valueClassName}>
      {value}
    </Text>
    <Text variant="secondary-sm" asSpan>
      {label}
    </Text>
  </Flex>
);

/**
 * The header "Status" stat: a colored icon + label for the package's review
 * state. Takes the already-resolved presentation (gate-aware; resolved by the
 * caller via `getPackageStatusPresentation`) rather than a raw `PackageStatus`,
 * so this component stays agnostic of the release gate.
 */
const StatusStat: React.FC<{ presentation: PackageStatusPresentation }> = ({
  presentation,
}) => {
  const { label, icon, color } = presentation;
  return (
    <Flex flexDirection="column" gap={2}>
      <span
        // "Confirmed issues" is also the label of the adjacent count stat, so
        // the state and the count are not distinguishable by text alone — tests
        // target the state through this handle.
        data-testid="package-status"
        className={styles.statusValue}
        style={assignInlineVars({ [styles.statusColorVar]: color })}
      >
        <span className={styles.statusRow}>
          <Icon type={icon} color={color} size="small" />
          <Text asSpan variant="strong-md">
            {label}
          </Text>
        </span>
      </span>
      <Text variant="secondary-sm" asSpan>
        Status
      </Text>
    </Flex>
  );
};

/**
 * PackageDetailSlideout — shows the deliverable package behind a (fund, period)
 * coverage cell, opened either by clicking that cell or by landing on a shared
 * deep link. Which package is open and which tab is showing are both owned by
 * the URL, so `packageId` and `activeTab` arrive as props. Hosts four tabs:
 * Summary, Checks, Documents, and Activity. Summary renders the package-level AI
 * executive summary (`package.executiveSummary`); Checks groups the package's
 * checks by document → section → check with triage; the Documents tab lists every
 * bound document, and selecting one opens its PDF beside the slideout; Activity
 * lists the source-linked activities behind the package.
 *
 * Activity is release-gated: it is only offered — and only rendered — when the
 * `fayActivityTracking` gate is on, so the other three are what a host without
 * the gate sees.
 */
export const PackageDetailSlideout: React.FC<PackageDetailSlideoutProps> = ({
  packageId,
  activeTab,
  onTabChange,
  onClose,
  onNotFound,
  arenaId,
}) => {
  // Fay MVP August release gate, read through the auth-gate context so this
  // component doesn't require a FlagProvider when rendered on its own — see
  // packageStatus/getPackageStatusPresentation for the off-path behavior.
  const fayMVPAugust = useFayAuthGateArmed();
  // Activity tab release gate, read through its derived context for the same
  // reason as the gate above — the raw flag hook would require a FlagProvider.
  const activityTabEnabled = useActivityTabEnabled();
  // Package-staging release gate, read through its derived context for the
  // same reason as the gates above — the raw flag hook would require a
  // FlagProvider.
  const fayPackageStaging = useFayPackageStaging();
  // The bound document whose PDF is open in the viewer, or null for the list.
  // Lives here, not in the tab, so it survives tab re-renders and resets cleanly
  // when a different package opens.
  const [activeDocument, setActiveDocument] = useState<PackageDocument | null>(null);
  // 1-based page the companion PDF lands on; set when a Checks section is opened,
  // undefined for a plain document open (defaults to page 1).
  const [pdfInitialPage, setPdfInitialPage] = useState<number | undefined>(undefined);
  // Document awaiting removal confirmation; null keeps the danger-confirm modal
  // closed.
  const [documentPendingRemoval, setDocumentPendingRemoval] = useState<PackageDocument | null>(
    null,
  );
  // Package whose Checks tab already auto-opened its first checked document,
  // and the package whose absence has already been reported up. Both guard
  // run-once effects below.
  const autoOpenedPackageRef = useRef<string | null>(null);
  const reportedNotFoundRef = useRef<string | null>(null);
  const {
    package: pkg,
    loading,
    error,
    notFound,
    refetch,
  } = usePackageDetail(packageId, arenaId);
  const { removeDocument, isRemoving } = useRemoveDocument(refetch, packageId, arenaId);
  const { setPublishState, isUpdating } = useUpdatePackageState(refetch, packageId, arenaId);
  const { download, downloadingDocumentId } = useDownloadPackageDocument(arenaId);

  // The tab is URL-owned, so a different package keeps whatever tab the URL
  // says; only the per-package document/modal state resets here, so the slideout
  // never reopens showing the previous package's PDF or a stale confirm modal.
  useEffect(() => {
    setActiveDocument(null);
    setPdfInitialPage(undefined);
    setDocumentPendingRemoval(null);
    autoOpenedPackageRef.current = null;
    reportedNotFoundRef.current = null;
  }, [packageId]);

  // The left companion PDF panel is live wherever a reviewer reads findings
  // against the source document: Documents (the user picks a doc), Checks (the
  // first checked document opens automatically — see below), and Activity (a
  // review note is raised against a document, so whatever either of those two
  // opened stays up on arrival). While it's up the drawer overlay goes
  // transparent (so the PDF stays visible beside the slideout) and
  // non-dismissable (so a click beside the panel doesn't close the slideout),
  // and the drawer narrows to the right half for a 50/50 split. Summary hides it
  // (the selection persists).
  const documentPdfOpen =
    (activeTab === "documents" || activeTab === "checks" || activeTab === "activity") &&
    activeDocument !== null;

  // First document that actually carries checks — the one the Checks tab opens in
  // the side PDF panel so the reviewer reads results next to the source document.
  const firstCheckDocument =
    pkg?.documents.find((doc) => doc.sections.some((section) => section.checks.length > 0)) ??
    null;

  // An effect rather than tab-click logic, so a cold deep link straight to
  // `.../checks` renders the same as clicking through: on that path the package
  // data arrives after mount, so `firstCheckDocument` is null at first and only
  // an effect can react when it appears. The ref scopes the auto-open to one
  // visit to the tab — without it, dismissing the PDF while sitting on Checks
  // would immediately reopen it — and clearing it on the way out means coming
  // back from another tab opens the PDF again, as it did before the tab moved
  // into the URL.
  useEffect(() => {
    if (activeTab !== "checks") {
      autoOpenedPackageRef.current = null;
      return;
    }
    if (activeDocument !== null || !firstCheckDocument) {
      return;
    }
    if (autoOpenedPackageRef.current === packageId) {
      return;
    }
    autoOpenedPackageRef.current = packageId;
    setActiveDocument(firstCheckDocument);
  }, [activeTab, activeDocument, firstCheckDocument, packageId]);

  // Ref-guarded so one bad id yields one toast even if `onNotFound` arrives as
  // an unstable inline prop. Cleared per package above, so returning to the
  // same bad id — a stale grid cell clicked twice, or Back onto the dead URL —
  // reports again rather than leaving the drawer open on nothing.
  useEffect(() => {
    if (!notFound || !packageId || reportedNotFoundRef.current === packageId) {
      return;
    }
    reportedNotFoundRef.current = packageId;
    onNotFound();
  }, [notFound, packageId, onNotFound]);

  return (
    <>
      <SlideoutDrawerV2
        isOpen={!!packageId}
        onClose={onClose}
        direction={Direction.Right}
        overlayStyle={documentPdfOpen ? OverlayStyle.Transparent : OverlayStyle.Opaque}
        isDismissable={!documentPdfOpen}
      >
        <SlideoutDrawerV2.ComplexHeader
          title={pkg ? packageTypeLabel(pkg.packageType) : "Package detail"}
          titleAdornment={
            // Publish state describes client visibility of a fund-admin
            // deliverable. On an entity the fund admin does not manage there is
            // nothing to publish, so the tag names a state that does not apply.
            fayPackageStaging && pkg?.isFaManaged ? (
              <Tag {...publishStateTag(pkg.publishState)} />
            ) : undefined
          }
          subtitles={
            packageId
              ? [
                  <Text key="entity-date" variant="secondary-md" asSpan>
                    {pkg
                      ? `${pkg.entityName} · ${formatReportingDate(pkg.reportingDate)}`
                      : "…"}
                  </Text>,
                ]
              : undefined
          }
        />
        <SlideoutDrawerV2.Body>
          <Flex flexDirection="column" className={styles.slideoutBody}>
            <Flex flexDirection="column" gap={16} flexShrink={0}>
              {pkg &&
                (() => {
                  const isPublished = pkg.publishState === "PUBLISHED";
                  const summary = packageSummary(pkg.documents, fayMVPAugust);
                  const status = packageStatus(
                    pkg.documents,
                    pkg.enumerationStatus,
                    fayMVPAugust,
                  );
                  return (
                    <Flex gap={32}>
                      <StatusStat
                        presentation={getPackageStatusPresentation(status, fayMVPAugust)}
                      />
                      <HeaderStat
                        label="Checks passed"
                        value={`${summary.passed} of ${summary.total}`}
                      />
                      <HeaderStat
                        label="Require review"
                        value={summary.requireReviewCount}
                        valueClassName={
                          summary.requireReviewCount > 0 ? styles.requireReviewCount : undefined
                        }
                      />
                      <HeaderStat
                        label="Confirmed issues"
                        value={summary.confirmedIssueCount}
                        valueClassName={
                          summary.confirmedIssueCount > 0
                            ? styles.confirmedIssueCount
                            : undefined
                        }
                      />
                      <HeaderStat label="Documents" value={summary.documentCount} />
                      {fayPackageStaging &&
                        hasPackageCapability(
                          pkg.actorCapabilities,
                          PackageCapability.Publish,
                        ) && (
                          <Flex alignItems="center" className={styles.publishAction}>
                            {/* The label names the setting and the knob carries
                                the state, so neither restates the status tag
                                beside the title. */}
                            <Switch
                              id="package-publish-state"
                              label={PUBLISH_SWITCH_LABEL}
                              labelPosition="left"
                              checked={isPublished}
                              disabled={isUpdating}
                              onChange={() => {
                                void setPublishState(isPublished ? "UNPUBLISHED" : "PUBLISHED");
                              }}
                            />
                          </Flex>
                        )}
                    </Flex>
                  );
                })()}

              <Tabs>
                <Tab
                  active={activeTab === "summary"}
                  text="Summary"
                  onClick={() => onTabChange("summary")}
                />
                <Tab
                  active={activeTab === "checks"}
                  text="Checks"
                  onClick={() => onTabChange("checks")}
                />
                <Tab
                  active={activeTab === "documents"}
                  text="Documents"
                  onClick={() => onTabChange("documents")}
                />
                {activityTabEnabled && (
                  <Tab
                    active={activeTab === "activity"}
                    text="Activity"
                    onClick={() => onTabChange("activity")}
                  />
                )}
              </Tabs>
            </Flex>

            <Flex
              flexDirection="column"
              gap={16}
              flex="1 1 auto"
              className={styles.slideoutScrollableContent}
            >
              {activeTab === "summary" && (
                <SummaryTab
                  summary={pkg?.executiveSummary ?? null}
                  publishState={pkg?.publishState ?? null}
                  publishStateChangedAt={pkg?.publishStateChangedAt ?? null}
                  isFaManaged={pkg?.isFaManaged ?? false}
                  enumerationStatus={pkg?.enumerationStatus ?? null}
                  summaryStatus={pkg?.executiveSummaryStatus ?? null}
                  loading={loading}
                  error={error}
                />
              )}

              {activeTab === "checks" && (
                <ChecksTab
                  packageId={packageId}
                  documents={pkg?.documents ?? []}
                  enumerationStatus={pkg?.enumerationStatus ?? null}
                  loading={loading}
                  error={error}
                  refetch={refetch}
                  arenaId={arenaId}
                  onDocumentOpen={(document) => {
                    // Expanding a document shows its PDF at page 1 (the default).
                    setActiveDocument(document);
                    setPdfInitialPage(undefined);
                  }}
                  onSectionOpen={(document, page) => {
                    setActiveDocument(document);
                    setPdfInitialPage(page);
                  }}
                />
              )}

              {activeTab === "documents" && (
                <PackageDocumentsTab
                  documents={pkg?.documents ?? []}
                  loading={loading}
                  error={error}
                  onSelectDocument={(doc) => {
                    setActiveDocument(doc);
                    // A plain document open starts at page 1, not a prior section page.
                    setPdfInitialPage(undefined);
                  }}
                  onRemoveDocument={setDocumentPendingRemoval}
                  // `download` reports its own failures through a toast and
                  // never rejects, so the promise is intentionally unawaited.
                  onDownloadDocument={(doc) => void download(doc)}
                  downloadingDocumentId={downloadingDocumentId}
                />
              )}

              {activityTabEnabled && activeTab === "activity" && (
                <ActivityTab packageId={packageId} arenaId={arenaId} />
              )}
            </Flex>
          </Flex>
        </SlideoutDrawerV2.Body>
      </SlideoutDrawerV2>
      <RemovePackageDocumentConfirmModal
        isOpen={documentPendingRemoval !== null}
        document={documentPendingRemoval}
        onClose={() => setDocumentPendingRemoval(null)}
        loading={isRemoving}
        onConfirm={() => {
          if (!documentPendingRemoval) {
            return;
          }
          // On success the payload package updates the slideout via the cache
          // and the coverage grid is nudged inside the hook; also close the
          // side PDF if it was showing the removed document. A conflict
          // (stale row, data refetching) closes the modal too; only a real
          // error keeps it open for retry.
          removeDocument(documentPendingRemoval.id)
            .then((outcome) => {
              if (outcome === "error") {
                return;
              }
              setDocumentPendingRemoval(null);
              if (outcome === "removed" && activeDocument?.id === documentPendingRemoval.id) {
                setActiveDocument(null);
              }
            })
            .catch(() => undefined);
        }}
      />
      {documentPdfOpen &&
        activeDocument &&
        createPortal(
          <div className={styles.companionPdfPanel}>
            <PdfViewer
              documentId={activeDocument.documentId}
              documentVersion={activeDocument.documentVersion}
              initialPage={pdfInitialPage}
              filename={activeDocument.filename}
              documentType={docTypeLabel(activeDocument.documentType) ?? undefined}
              onBack={() => setActiveDocument(null)}
              arenaId={arenaId}
            />
          </div>,
          document.body,
        )}
    </>
  );
};
